/**
 * Shared sanitiser + parser for Cashier "amount" fields (deposit card, deposit
 * cage, withdraw). The raw `<input type="number">` accepted `-`, `+`, `e` and a
 * bare `.` as "bad input": the visible text kept the garbage while `.value`
 * silently became '', so a previously-selected preset stayed applied and the
 * player could Continue with a nonsense-looking field. These helpers back a
 * plain `type="text"` field instead, so what the player sees is exactly what we
 * parse.
 */

/**
 * Strip everything that can't belong to a currency amount, keeping only digits
 * and a single decimal point with at most two fractional digits.
 *
 * Blocks `-`, `+`, `e`/`E`, letters, currency symbols and whitespace outright.
 * A leading/lone `.` is preserved so the player can type ".5" or "12."
 * naturally — but a bare "." parses to an incomplete amount (see
 * parseCurrencyAmount), which the callers treat as "not continuable".
 *
 * Examples: "-" → "", "1+2" → "12", "12.3.4" → "12.34", "12.345" → "12.34",
 * "5." → "5.", "." → ".".
 */
export function sanitizeCurrencyAmount(raw: string): string {
  if (!raw) return '';
  // Drop any character that isn't a digit or a dot (removes - + e E $ , spaces…).
  let cleaned = raw.replace(/[^\d.]/g, '');

  // Collapse to a single decimal point — keep the first, fold the rest away.
  const firstDot = cleaned.indexOf('.');
  if (firstDot !== -1) {
    const intPart = cleaned.slice(0, firstDot);
    const fracPart = cleaned.slice(firstDot + 1).replace(/\./g, '');
    // Limit the fractional part to two digits (currency precision).
    cleaned = `${intPart}.${fracPart.slice(0, 2)}`;
  }

  return cleaned;
}

/**
 * Numeric value of a sanitised amount string. Incomplete values — '', '.' — are
 * NOT valid amounts and resolve to 0 so the caller clears any selected preset
 * and keeps Continue disabled. "5." → 5, ".5" → 0.5, "12.50" → 12.5.
 */
export function parseCurrencyAmount(cleaned: string): number {
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}
